import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { PDFCheckBox, PDFDocument, PDFDropdown, PDFField, PDFOptionList, PDFRadioGroup, PDFTextField } from 'pdf-lib';
import { InvestigationRequest } from 'vl-common/src/schemas/shared/investigationRequest';

// Cypress needs a handle on this so that I can mock it
if (typeof window !== 'undefined') {
  window.PDFDocument = PDFDocument;
}

export interface PDFDocumentMeta {
  status: number;
  edited_flag: boolean;
  signature_x: number;
  signature_y: number;
  signature_page: number;
  base64: string;
}

/**
Things to know about Investigation Requests
An Investigation Request ("IR") represents a diagnostic test issued by a clinician during an appointment. They used to
be limited to imaging requests (think, X-Rays, CT Scans etc) but now include blood tests, smears etc.

Ultimately the IR manifests itself as a PDF that gets sent to a suitable chosen hospital department telling them what to
do and how to contact the patient in order to arrange their appointment. This PDF is generated in two ways ... either
from a PDF form or from a SurveyJS form. PDFs derived from PDF forms need to be flattened (have all their form elements
replaced by their values) in order to ensure they cannot be tampered with.

The IRs are stored in the backend, but the generated PDFs are also stored as ".pdf" documents in S3. We do not currently
store the PDF form values anywhere other than in the un-flattened PDF stored in S3.
*/
export function useInvestigationRequestPDFUtils(appointmentId: number) {
  const dispatch = useDispatch();

  return {
    /** Loads all the InvestigationRequest database objects. Calls onBadInvestigationRequest if any of them look bad */
    async loadInvestigationRequests(onBadInvestigationRequest: () => void) {
      const investigationRequestResponse = await dispatch(actions.clinicianInvReqByApptId(1, appointmentId));
      const investigationRequests = (investigationRequestResponse?.invreqs || []) as InvestigationRequest[];

      if (
        investigationRequests.some(
          ({ service_type_code, urgency_code, hospital_code, ireq_id }) =>
            !service_type_code || !urgency_code || !hospital_code || !ireq_id
        )
      ) {
        onBadInvestigationRequest();
        return [] as InvestigationRequest[];
      }

      return investigationRequests as InvestigationRequest[];
    },

    /** Loads the PDFDocument for the given appointment and investigation request id. Not sure what this does for Surveys */
    async loadPDFDocument(ireq_id: number) {
      const { payload, type } = await dispatch(actions.getSavedPDF(appointmentId, ireq_id));

      if (type === actionTypes.GET_PDF_FORM_FAIL || !payload.base64)
        return { error: new Error(), document: undefined, meta: undefined };

      return { error: undefined, document: await PDFDocument.load(payload.base64), meta: payload as PDFDocumentMeta };
    },

    /** Returns a list of field names that are required but have no value */
    getMissingFields(doc: PDFDocument) {
      const newForm = doc.getForm();
      const requiredFields = newForm.getFields().filter((f) => f.isRequired());

      const missingFields = requiredFields
        .map((r) => {
          // see https://pdf-lib.js.org/docs/api/classes/pdffield#hierarchy
          if (r instanceof PDFOptionList || r instanceof PDFRadioGroup) {
            return r.getSelected() ? null : r;
          }

          if (r instanceof PDFCheckBox) {
            return null;
          }

          if (r instanceof PDFDropdown) {
            return r.getSelected().length > 0 ? null : r;
          }

          if (r instanceof PDFTextField) {
            return r.getText() ? null : r;
          }

          // ... leaving PDFButton and PDFSignature
          console.error('Unanticipated Form Element Type', r);
          return r;
        })
        .filter((m) => m) as PDFField[];

      return missingFields.map((f) => f.getName());
    },

    /** Flattens the given document and saves it back to S3 */
    async flattenAndSave(doc: PDFDocument, ireq_id: number) {
      const newForm = doc.getForm();

      newForm.flatten();
      const currentPDFState = {
        appt_id: appointmentId,
        ireq_id,
        response_json: null,
        base64: await doc.saveAsBase64()
      };

      const { status } = await dispatch(actions.postPDFState(appointmentId, ireq_id, currentPDFState));

      return status === 200;
    }
  };
}
